import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { ModalTypes } from "@enums/modal-types";
import { useModalContext } from "@context/use-modal-context";
import {
    getIdentityProviderConfigurationsReduxRequest,
    getIdentityProvidersReferenceDataReduxRequest
} from "@redux-requests/identity-providers";
import { ContentPageHeader } from "../common/content-page-header/content-page-header";
import { ButtonWithIcon } from "../common/button-with-icon/button-with-icon";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { AddIdentityProviderModal } from "../modals/add-identity-provider-modal/add-identity-provider-modal";
import { IdentityProvidersTableContainer } from "./identity-providers-table-container/identity-providers-table-container";
import { CardContainer } from "../common/card-container/card-container";
import { ContentPageBody } from "../common/content-page-body/content-page-body";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getIdentityProvidersDataState, getIdentityProvidersReferenceDataState } from "@redux/selectors/identity-providers-selectors";

export const IdentityProvidersContainer = () => {
    const modalContext = useModalContext();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { identityProviders: identityProvidersTableState } = useSelector(getTableSettingsState);
    const referenceData = useSelector(getIdentityProvidersReferenceDataState);
    const {
        isLoadingIdentityProviderConfigurations,
        identityProviderConfigurations
    } = useSelector(getIdentityProvidersDataState);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadIdentityProviderConfigurations, setShouldLoadIdentityProviderConfigurations] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getIdentityProvidersReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.categories &&
            referenceData.classifications &&
            referenceData.companies &&
            referenceData.loas &&
            referenceData.protocols
        );
    }, [
        referenceData.categories,
        referenceData.classifications,
        referenceData.companies,
        referenceData.loas,
        referenceData.protocols
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingIdentityProviderConfigurations && shouldLoadIdentityProviderConfigurations) {
            setShouldLoadIdentityProviderConfigurations(false);
            getIdentityProviderConfigurationsReduxRequest(dispatch)(
                referenceData.categories,
                referenceData.classifications,
                referenceData.companies,
                referenceData.loas,
                referenceData.protocols
            );
        }
    }, [isReferenceDataPresent, isLoadingIdentityProviderConfigurations, shouldLoadIdentityProviderConfigurations]);

    const renderAddIdentityProviderModal = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddIdentityProviderModal
                onModalClose={onModalClose}
                identityProviderConfigurations={identityProviderConfigurations}
            />
        );
    }, [identityProviderConfigurations]);

    const isLoading = useMemo(() => {
        return isLoadingIdentityProviderConfigurations || referenceData.isLoading;
    }, [referenceData.isLoading, isLoadingIdentityProviderConfigurations]);

    const isLoaded = useMemo(() => {
        return !!(!isLoading &&
            isReferenceDataPresent &&
            identityProviderConfigurations &&
            !shouldLoadIdentityProviderConfigurations &&
            !shouldLoadReferenceData);
    }, [isLoading, isReferenceDataPresent, identityProviderConfigurations, shouldLoadIdentityProviderConfigurations, shouldLoadReferenceData]);

    const headerButton = useMemo(() => {
        if (session.userPermissions.canCreateIdentityProviders) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddIdentityProvider,
                    renderFunction: renderAddIdentityProviderModal
                });
            };
            return (
                <ButtonWithIcon
                    id="add-identity-provider-button"
                    title={resources.pages.identityProviders.headerButton}
                    text={resources.pages.identityProviders.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        } else {
            return null;
        }
    }, [isLoading, isLoaded, session, renderAddIdentityProviderModal]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader title={resources.pages.identityProviders.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <IdentityProvidersTableContainer
                        identityProviderConfigurations={identityProviderConfigurations}
                        tableState={identityProvidersTableState}
                        isPageHeight={true}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};